import { useEffect, useState, useRef } from "react";
import { RefreshCw, Database, AlertCircle, CheckCircle2, Play, Boxes, Wallet } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";

const STATUS_LABELS = {
  never: { label: "Sin sincronizar", color: "bg-cm-cream-alt text-cm-ink-soft", icon: AlertCircle },
  running: { label: "Sincronizando", color: "bg-blue-100 text-blue-800", icon: RefreshCw },
  done: { label: "Completado", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  error: { label: "Con errores", color: "bg-red-100 text-red-800", icon: AlertCircle },
};

export default function AdminCatalog() {
  const [status, setStatus] = useState(null);
  const [carteraStatus, setCarteraStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [carteraLoading, setCarteraLoading] = useState(false);
  const pollRef = useRef(null);
  const carteraPollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await adminApi.get("/admin/sync-status");
      setStatus(data);
      if (data.in_progress && !pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 3000);
      }
      if (!data.in_progress && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch {
      setStatus(null);
    }
  };

  const fetchCarteraStatus = async () => {
    try {
      const { data } = await adminApi.get("/admin/cartera/sync-status");
      setCarteraStatus(data);
      const running = data.status === "running";
      if (running && !carteraPollRef.current) {
        carteraPollRef.current = setInterval(fetchCarteraStatus, 4000);
      }
      if (!running && carteraPollRef.current) {
        clearInterval(carteraPollRef.current);
        carteraPollRef.current = null;
      }
    } catch {
      setCarteraStatus(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchCarteraStatus();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (carteraPollRef.current) clearInterval(carteraPollRef.current);
    };
  }, []);

  const startSync = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/sync-catalog");
      if (data.status === "started") toast.success("Sincronización iniciada");
      else toast.info("Ya hay una sincronización en curso");
      await fetchStatus();
    } catch (err) {
      toast.error("Error al iniciar sincronización");
    } finally { setLoading(false); }
  };

  const refreshInventory = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.post("/admin/sync-inventory");
      if (data.error) toast.error(`Error: ${data.error}`);
      else toast.success(`Inventario actualizado: ${data.updated} productos (${data.fetched} saldos de bodega ${data.bodega})`);
      await fetchStatus();
    } catch (err) {
      toast.error("Error al actualizar inventario");
    } finally { setLoading(false); }
  };

  const startCarteraSync = async () => {
    setCarteraLoading(true);
    try {
      const { data } = await adminApi.post("/admin/cartera/sync");
      if (data.status === "started") toast.success("Sincronización de cartera iniciada — puede tomar 5-10 minutos");
      else if (data.status === "already_running") toast.info("Ya hay una sincronización en curso");
      else toast.success("Sync ejecutado");
      await fetchCarteraStatus();
    } catch (err) {
      toast.error("Error al iniciar sync de cartera");
    } finally { setCarteraLoading(false); }
  };

  const st = status?.status || "never";
  const cfg = STATUS_LABELS[st] || STATUS_LABELS.never;
  const Icon = cfg.icon;
  const progress = status && status.total_estimated
    ? Math.min(100, Math.round(((status.processed || 0) / status.total_estimated) * 100))
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-catalog-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Catálogo</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Sincronización Arcos</h1>
          <p className="text-sm text-cm-ink-soft mt-1">
            Importa todos los productos activos y sus listas de precios desde Arcos ERP.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={fetchStatus} variant="outline" className="border-cm-ink hover:bg-cm-ink hover:text-cm-cream" data-testid="refresh-sync-status">
            <RefreshCw className="h-4 w-4 mr-2" /> Actualizar
          </Button>
          <Button
            onClick={refreshInventory}
            disabled={loading || status?.in_progress}
            variant="outline"
            className="border-cm-yellow bg-cm-yellow/20 hover:bg-cm-yellow text-cm-ink"
            data-testid="refresh-inventory-btn"
          >
            <Boxes className="h-4 w-4 mr-2" /> Refrescar inventario
          </Button>
          <Button
            onClick={startSync}
            disabled={loading || status?.in_progress}
            className="btn-cm-primary"
            data-testid="start-sync-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            {status?.in_progress ? "En progreso..." : "Sincronizar catálogo"}
          </Button>
        </div>
      </div>

      <Card className="bg-white border-cm-border shadow-none p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-md bg-cm-cream-alt flex items-center justify-center flex-shrink-0">
            <Database className="h-6 w-6 text-cm-red" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={`${cfg.color} border-0`}>
                <Icon className={`h-3 w-3 mr-1 ${st === "running" ? "animate-spin" : ""}`} />
                {cfg.label}
              </Badge>
              {status?.cached_count !== undefined && (
                <span className="text-sm text-cm-ink-soft" data-testid="cached-count">
                  · <span className="font-mono font-semibold text-cm-ink">{status.cached_count}</span> productos en caché
                </span>
              )}
            </div>
            <h2 className="font-serif text-lg mt-2">Estado de la sincronización</h2>
            <p className="text-xs text-cm-ink-soft mt-1">
              {status?.started_at ? `Iniciado: ${new Date(status.started_at).toLocaleString("es-CO")}` : "Aún no se ha ejecutado"}
              {status?.finished_at && ` · Finalizado: ${new Date(status.finished_at).toLocaleString("es-CO")}`}
            </p>
          </div>
        </div>

        {(status?.in_progress || st === "running") && (
          <div className="mt-6 space-y-2" data-testid="sync-progress-block">
            <div className="flex justify-between text-sm">
              <span className="text-cm-ink-soft">Procesando productos...</span>
              <span className="font-mono font-semibold">{status.processed || 0} / {status.total_estimated || "…"}</span>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-cm-ink-soft">
              Páginas leídas: <span className="font-mono">{status.pages || 0}</span>
              {status.errors > 0 && <span className="text-cm-red ml-2">· Errores: {status.errors}</span>}
            </p>
          </div>
        )}

        {st === "done" && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="sync-summary">
            <div className="bg-cm-cream-alt/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Páginas</p>
              <p className="font-mono text-xl font-semibold mt-1">{status.pages}</p>
            </div>
            <div className="bg-cm-cream-alt/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Listados</p>
              <p className="font-mono text-xl font-semibold mt-1">{status.total_listed}</p>
            </div>
            <div className="bg-cm-cream-alt/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Activos</p>
              <p className="font-mono text-xl font-semibold mt-1">{status.total_active}</p>
            </div>
            <div className="bg-cm-cream-alt/40 rounded-md p-3">
              <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Importados</p>
              <p className="font-mono text-xl font-semibold mt-1 text-green-700">{status.processed}</p>
            </div>
          </div>
        )}

        {st === "error" && status.error && (
          <div className="mt-6 bg-cm-red/5 border border-cm-red/30 rounded-md p-4" data-testid="sync-error-block">
            <p className="text-sm font-semibold text-cm-red">Error en la sincronización</p>
            <p className="text-xs font-mono text-cm-red mt-1 break-words">{status.error}</p>
          </div>
        )}
      </Card>

      {/* ---------- Cartera Sync ---------- */}
      <Card className="bg-white border-cm-border shadow-none p-6" data-testid="cartera-sync-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-12 w-12 rounded-md bg-cm-yellow/20 flex items-center justify-center flex-shrink-0">
            <Wallet className="h-6 w-6 text-cm-red" />
          </div>
          <div className="flex-1 min-w-[240px]">
            <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Cartera · facturas vencidas</p>
            <h2 className="font-serif text-xl mt-1">Sincronizar cartera con Arcos</h2>
            <p className="text-sm text-cm-ink-soft mt-2 max-w-2xl">
              Consulta <code className="font-mono text-cm-ink bg-cm-cream-alt px-1.5 py-0.5 rounded text-xs">ReciboCaja/ListCartera</code> para
              cada cliente activo y refresca cupo, saldo y facturas vencidas. Usalo si en producción los clientes ven datos
              desactualizados (todos marcados como al día cuando deberían tener vencidas).
            </p>
            {carteraStatus && (
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline" className={
                  carteraStatus.status === "running" ? "bg-blue-100 text-blue-800 border-0"
                  : carteraStatus.status === "done" ? "bg-green-100 text-green-800 border-0"
                  : carteraStatus.status === "error" ? "bg-red-100 text-red-800 border-0"
                  : "bg-cm-cream-alt text-cm-ink-soft border-0"
                }>
                  {carteraStatus.status === "running" ? "Sincronizando..." :
                   carteraStatus.status === "done" ? "Completado" :
                   carteraStatus.status === "error" ? "Error" : "Sin sincronizar"}
                </Badge>
                {carteraStatus.cached_count !== undefined && (
                  <span className="text-cm-ink-soft">
                    · <span className="font-mono font-semibold text-cm-ink">{carteraStatus.cached_count}</span> clientes en caché
                  </span>
                )}
                {carteraStatus.finished_at && (
                  <span className="text-cm-ink-soft">
                    · Último sync: {new Date(carteraStatus.finished_at).toLocaleString("es-CO")}
                  </span>
                )}
              </div>
            )}
            {carteraStatus?.status === "done" && (carteraStatus.real_data_count !== undefined) && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="cartera-sync-summary">
                <div className="bg-cm-cream-alt/40 rounded-md p-2.5">
                  <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Procesados</p>
                  <p className="font-mono text-lg font-semibold mt-0.5">{carteraStatus.processed || 0}</p>
                </div>
                <div className={`rounded-md p-2.5 ${carteraStatus.real_data_count > 0 ? "bg-green-50" : "bg-red-50"}`}>
                  <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Reales Arcos</p>
                  <p className={`font-mono text-lg font-semibold mt-0.5 ${carteraStatus.real_data_count > 0 ? "text-green-700" : "text-red-700"}`}>
                    {carteraStatus.real_data_count || 0}
                  </p>
                </div>
                <div className={`rounded-md p-2.5 ${carteraStatus.seed_data_count > 0 ? "bg-red-50" : "bg-cm-cream-alt/40"}`}>
                  <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Seed (fallback)</p>
                  <p className={`font-mono text-lg font-semibold mt-0.5 ${carteraStatus.seed_data_count > 0 ? "text-red-700" : ""}`}>
                    {carteraStatus.seed_data_count || 0}
                  </p>
                </div>
                <div className="bg-cm-cream-alt/40 rounded-md p-2.5">
                  <p className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Errores</p>
                  <p className="font-mono text-lg font-semibold mt-0.5">{carteraStatus.errors || 0}</p>
                </div>
              </div>
            )}
            {carteraStatus?.status === "done" && carteraStatus.seed_data_count > 0 && carteraStatus.real_data_count === 0 && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-800" data-testid="cartera-arcos-unreachable">
                <strong>⚠️ Arcos no respondió `ReciboCaja/ListCartera` para ningún cliente.</strong> Todos cayeron en modo seed (fallback &quot;Al día&quot;).
                Probable causa: IP del servidor de producción no autorizada en Arcos, o credenciales/token expirados en <code className="font-mono">backend/.env</code>.
              </div>
            )}
          </div>
          <Button
            onClick={startCarteraSync}
            disabled={carteraLoading || carteraStatus?.status === "running"}
            className="btn-cm-primary"
            data-testid="start-cartera-sync-btn"
          >
            <Play className="h-4 w-4 mr-2" />
            {carteraStatus?.status === "running" ? "Sincronizando..." : "Sincronizar cartera"}
          </Button>
        </div>
      </Card>

      <Card className="bg-cm-cream-alt/40 border-cm-border shadow-none p-6">
        <h3 className="font-serif text-base mb-2">¿Cómo funciona?</h3>
        <ol className="space-y-2 text-sm text-cm-ink-soft list-decimal pl-5">
          <li><strong>Sincronizar catálogo</strong>: consulta <code className="font-mono text-cm-ink bg-white px-1.5 py-0.5 rounded text-xs">Producto/List</code> paginando todos los activos y <code className="font-mono text-cm-ink bg-white px-1.5 py-0.5 rounded text-xs">Producto/Get/{"{id}"}</code> para sus precios por lista. Toma ~6 minutos.</li>
          <li><strong>Refrescar inventario</strong>: trae todos los saldos de la bodega de venta en <strong>una sola llamada</strong> a <code className="font-mono text-cm-ink bg-white px-1.5 py-0.5 rounded text-xs">ListInventarioWS?BodegaId=1</code>. Toma ~1 segundo. Útil durante el día.</li>
          <li><strong>Solo se muestran al cliente</strong> los productos con stock &gt; 0 en la bodega configurada.</li>
          <li>Hay un <strong>sync automático todos los días a las 2:00 AM</strong> hora Colombia (incluye catálogo + inventario).</li>
        </ol>
      </Card>
    </div>
  );
}
